import { useCallback, useEffect, useState } from "react";
import {
  extractKbFields,
  getGameRuleHealth,
  saveKbSection,
} from "../services/gameRuleKbService";
import type { KbDraftField } from "../types/gameRuleKb";
import type { ContentStructure } from "../types/kbContentStructure";
import {
  buildKbSectionSavePayload,
  createEmptyKbDraftField,
  mapExtractFieldToDraft,
  untaggedFieldNames,
} from "../utils/kbFieldMapper";
import { plainTextToStructure } from "../utils/kbStructurePlain";

export type KbExtractStatus = "idle" | "loading" | "ready" | "error";
export type KbHealthStatus = "unknown" | "ok" | "degraded" | "error";

export const useCreateKbSectionWizard = () => {
  const [contentPlain, setContentPlain] = useState("");
  const [contentStructure, setContentStructure] = useState<ContentStructure | null>(null);
  const [extractedContent, setExtractedContent] = useState<string | null>(null);
  const [extractedStructure, setExtractedStructure] = useState<Record<string, unknown> | null>(
    null,
  );
  const [sectionName, setSectionName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [fields, setFields] = useState<KbDraftField[]>([]);
  const [extractStatus, setExtractStatus] = useState<KbExtractStatus>("idle");
  const [healthStatus, setHealthStatus] = useState<KbHealthStatus>("unknown");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedSectionCount, setSavedSectionCount] = useState(0);
  const [lastSavedSectionId, setLastSavedSectionId] = useState<string | null>(null);
  const [lastSavedVersion, setLastSavedVersion] = useState<number | null>(null);
  const [lastSaveWasVersionUpdate, setLastSaveWasVersionUpdate] = useState(false);
  const [showPostSaveActions, setShowPostSaveActions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getGameRuleHealth()
      .then((health) => {
        if (cancelled) {
          return;
        }
        setHealthStatus(health.status === "ok" ? "ok" : "degraded");
      })
      .catch(() => {
        if (!cancelled) {
          setHealthStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setTag = useCallback((name: string) => {
    setSelectedTag(name.trim());
  }, []);

  const clearTag = useCallback(() => {
    setSelectedTag("");
  }, []);

  const updateField = useCallback((id: string, patch: Partial<KbDraftField>) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  const removeField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const upsertField = useCallback((field: KbDraftField) => {
    setFields((prev) => {
      const index = prev.findIndex((f) => f.id === field.id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = field;
        return next;
      }
      return [...prev, field];
    });
  }, []);

  const setContentPlainAndStructure = useCallback((plain: string) => {
    const trimmed = plain.trim();
    setContentPlain(trimmed);
    setContentStructure(trimmed ? plainTextToStructure(trimmed) : null);
  }, []);

  const runExtract = useCallback(async (plain: string, name: string) => {
    setError(null);
    setExtractStatus("loading");
    setShowPostSaveActions(false);
    const structure = plainTextToStructure(plain, name);
    setContentStructure(structure);
    try {
      const result = await extractKbFields(plain, name, structure);
      const mapped = result.fields.map(mapExtractFieldToDraft);
      setFields(mapped.length > 0 ? mapped : [createEmptyKbDraftField()]);
      setExtractedContent(result.extractedContent);
      setExtractedStructure(result.extractedStructure);
      if (result.contentStructure) {
        setContentStructure(result.contentStructure as ContentStructure);
      }
      setExtractStatus("ready");
    } catch (e: unknown) {
      setExtractStatus("error");
      setFields([createEmptyKbDraftField()]);
      setExtractedContent(null);
      setExtractedStructure(null);
      setError(e instanceof Error ? e.message : "Field extraction failed.");
    }
  }, []);

  const validateStep2 = useCallback((plain: string, name: string, tag: string): string | null => {
    if (!plain.trim() || plain.trim().length < 10) {
      return "Select meaningful clause text in Word (at least 10 characters).";
    }
    if (!name.trim()) {
      return "Section name is required.";
    }
    if (!tag.trim()) {
      return "Choose exactly one tag for this section.";
    }
    return null;
  }, []);

  const saveSection = useCallback(async (): Promise<boolean> => {
    setError(null);
    const untagged = untaggedFieldNames(fields);
    if (untagged.length > 0) {
      setError(`Link these fields before save: ${untagged.join(", ")}`);
      return false;
    }
    setBusy(true);
    try {
      const payload = buildKbSectionSavePayload({
        sectionName,
        description,
        contentPlain,
        extractedContent,
        contentStructure,
        extractedStructure,
        tagNames: selectedTag ? [selectedTag] : [],
        fields,
      });
      const saved = await saveKbSection(payload);
      setSavedSectionCount((c) => c + 1);
      setLastSavedSectionId(saved.id);
      setLastSavedVersion(saved.version);
      setLastSaveWasVersionUpdate(saved.is_version_update);
      setShowPostSaveActions(true);
      return true;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [
    contentPlain,
    contentStructure,
    description,
    extractedContent,
    extractedStructure,
    fields,
    sectionName,
    selectedTag,
  ]);

  const resetForAnotherSection = useCallback(() => {
    setContentPlain("");
    setContentStructure(null);
    setExtractedContent(null);
    setExtractedStructure(null);
    setSectionName("");
    setDescription("");
    setSelectedTag("");
    setFields([]);
    setExtractStatus("idle");
    setError(null);
    setShowPostSaveActions(false);
    setLastSavedVersion(null);
    setLastSaveWasVersionUpdate(false);
  }, []);

  return {
    contentPlain,
    setContentPlain: setContentPlainAndStructure,
    contentStructure,
    extractedContent,
    extractedStructure,
    sectionName,
    setSectionName,
    description,
    setDescription,
    selectedTag,
    setTag,
    clearTag,
    fields,
    updateField,
    removeField,
    upsertField,
    extractStatus,
    healthStatus,
    busy,
    error,
    setError,
    savedSectionCount,
    lastSavedSectionId,
    lastSavedVersion,
    lastSaveWasVersionUpdate,
    showPostSaveActions,
    runExtract,
    validateStep2,
    saveSection,
    resetForAnotherSection,
  };
};
